import React from 'react';
import {
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  ChartBarIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ShareIcon,
  TrophyIcon,
  SparklesIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../../api/client';
import { formatDuration } from '../../../utils/format';
import { qaAnswerHtml } from '../../../components/markdown/qaAnswerHtml';

const reportKindLabel = (item) => (item.report_kind === 'daily' ? `Day ${item.day_number}` : 'Full event');

export const reportOptionLabel = (item) =>
  `${reportKindLabel(item)}${item.is_official ? ' · official' : ''}${item.status !== 'COMPLETED' ? ` · ${item.status.toLowerCase()}` : ''}`;

/** "Full event · official" for the selected stored report, else from the report itself. */
export const reportHeading = (report, selectedMeta) => {
  if (selectedMeta) return `${reportKindLabel(selectedMeta)}${selectedMeta.is_official ? ' · official' : ''}`;
  return report.reportType === 'daily' ? `Day ${report.day}` : 'Full event';
};

export const StatsNote = ({ Icon, spin = false, children }) => (
  <div className="event-stats-note">
    <Icon className={spin ? 'event-stats-spin' : undefined} />
    <span>{children}</span>
  </div>
);

export const ActionButton = ({ onClick, disabled, busy, done = false, Icon, label, busyLabel, doneLabel }) => (
  <button type="button" className="event-detail-btn" onClick={onClick} disabled={disabled}>
    {busy ? <ArrowPathIcon className="event-stats-spin" /> : done ? <CheckCircleIcon /> : <Icon />}
    {busy ? busyLabel : done ? doneLabel : label}
  </button>
);

export const StatsMessage = ({ kind, Icon, children }) => (
  <div className={`event-stats-msg event-stats-msg-${kind}`} style={{ marginTop: 14 }}>
    <Icon />
    <span>{children}</span>
  </div>
);

const StatTile = ({ label, value }) => (
  <div>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

export const StatsStrips = ({ stats }) => (
  <>
    <div className="event-stats-strip">
      <StatTile label="Unique speakers" value={stats.uniqueSpeakers || 0} />
      <StatTile label="Total speeches" value={stats.totalSpeeches?.toLocaleString() || 0} />
      <StatTile label="Total time" value={stats.totalTimeDescription || formatDuration(stats.totalTime)} />
      <StatTile label="Total words" value={stats.totalLength?.toLocaleString() || 0} />
    </div>
    <div className="event-stats-strip">
      <StatTile label="Agreed points" value={stats.agreedPoints || 0} />
      <StatTile label="Points of difference" value={stats.pointsOfDifference || 0} />
      <StatTile label="Thought-provoking comments" value={stats.thoughtProvokingComments || 0} />
    </div>
  </>
);

export const NotableSessions = ({ notable }) => {
  const highlights = [
    { label: 'Longest session', session: notable.longestSession },
    { label: 'Most speakers', session: notable.mostSpeakersSession, suffix: 'speakers' },
    { label: 'Most words', session: notable.mostWordsSession, suffix: 'words' },
    { label: 'Most arguments', session: notable.mostArgumentsSession, suffix: 'arguments' },
  ].filter((item) => item.session);
  if (highlights.length === 0) return null;

  return (
    <div className="event-stats-card">
      <h3><TrophyIcon /> Notable sessions</h3>
      <div className="event-stats-grid">
        {highlights.map(({ label, session, suffix }) => (
          <div key={label} className="event-stats-highlight">
            <span>{label}</span>
            <strong>{session.sessionName}</strong>
            <em>
              {typeof session.value === 'number' ? session.value.toLocaleString() : session.value}
              {typeof session.value === 'number' && suffix ? ` ${suffix}` : ''}
            </em>
          </div>
        ))}
      </div>
    </div>
  );
};

export const FastestSpeakers = ({ speakers }) => (
  <div className="event-stats-card">
    <h3><ChartBarIcon /> Fastest speakers</h3>
    <div className="event-stats-table-wrap">
      <table className="event-stats-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Speaker</th>
            <th>Speed</th>
          </tr>
        </thead>
        <tbody>
          {speakers.slice(0, 10).map((speaker) => (
            <tr key={speaker.position}>
              <td>{speaker.position}</td>
              <td>{speaker.speakerName}</td>
              <td>{speaker.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const TopKeywords = ({ prefixes }) => (
  <div className="event-stats-card">
    <h3><SparklesIcon /> Top keywords</h3>
    <div className="event-stats-chips">
      {prefixes.map((prefix, idx) => (
        <span key={`${prefix.prefixName}-${idx}`}>
          {prefix.prefixName}
          <small>{prefix.eventValue}</small>
        </span>
      ))}
    </div>
  </div>
);

export const QaAnalysis = ({ questions, expandedIndex, onToggle }) => (
  <div className="event-stats-card">
    <h3><LightBulbIcon /> Q&amp;A analysis</h3>
    <div className="event-stats-qa">
      {questions.map((qa, idx) => (
        <div key={idx} className="event-stats-qa-item">
          <button type="button" onClick={() => onToggle(idx)}>
            <span>{qa.question}</span>
            {expandedIndex === idx ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </button>
          {expandedIndex === idx && (
            <div
              className="event-stats-qa-answer"
              dangerouslySetInnerHTML={{ __html: qaAnswerHtml(qa.answer) }}
            />
          )}
        </div>
      ))}
    </div>
  </div>
);

export const ChatbotEmbed = ({ src }) => (
  <div className="event-stats-card event-stats-embed">
    <div className="event-stats-embed-head">
      <ChatBubbleLeftRightIcon /> Event chatbot
    </div>
    <iframe src={src} title="Event Chatbot" allow="microphone" />
  </div>
);

export const GraphEmbed = ({ path }) => (
  <div className="event-stats-card event-stats-embed">
    <div className="event-stats-embed-head">
      <ShareIcon /> Knowledge graph
    </div>
    <iframe src={path.startsWith('http') ? path : `${API_BASE_URL}${path}`} title="Knowledge Graph" />
  </div>
);
